import {
  getLogger
} from "./chunk-LFIBM62H.js";

// src/adapter/machine/adapter-state.ts
var AdapterState = /* @__PURE__ */ ((AdapterState2) => {
  AdapterState2["Idle"] = "idle";
  AdapterState2["Playing"] = "playing";
  AdapterState2["Paused"] = "paused";
  AdapterState2["AutoplayBlocked"] = "autoplayblocked";
  AdapterState2["Broken"] = "broken";
  AdapterState2["Starting"] = "starting";
  AdapterState2["Seeking"] = "seeking";
  AdapterState2["Rebuffering"] = "rebuffering";
  AdapterState2["PausedSeeking"] = "paused-seeking";
  AdapterState2["Ended"] = "ended";
  return AdapterState2;
})(AdapterState || {});
function adapterToPlaybackState(adapterState) {
  const map = {
    ["idle" /* Idle */]: { playbackState: "idle" /* Idle */ },
    ["playing" /* Playing */]: { playbackState: "playing" /* Playing */ },
    ["paused" /* Paused */]: { playbackState: "paused" /* Paused */ },
    ["autoplayblocked" /* AutoplayBlocked */]: {
      playbackState: "paused" /* Paused */,
      pausedReason: "autoplayblocked" /* AutoplayBlocked */
    },
    ["broken" /* Broken */]: { playbackState: "broken" /* Broken */ },
    ["starting" /* Starting */]: {
      playbackState: "playing" /* Playing */,
      waitingReason: "starting" /* Starting */
    },
    ["seeking" /* Seeking */]: {
      playbackState: "playing" /* Playing */,
      waitingReason: "seeking" /* Seeking */
    },
    ["rebuffering" /* Rebuffering */]: {
      playbackState: "playing" /* Playing */,
      waitingReason: "rebuffering" /* Rebuffering */
    },
    ["paused-seeking" /* PausedSeeking */]: {
      playbackState: "paused" /* Paused */,
      waitingReason: "seeking" /* Seeking */
    },
    ["ended" /* Ended */]: {
      playbackState: "paused" /* Paused */,
      pausedReason: "ended" /* Ended */
    }
  };
  return map[adapterState];
}

// src/adapter/machine/index.ts
import { TypeState } from "typestate";

// src/adapter/machine/state-transitions.ts
function addStateTransitions(stateMachine) {
  stateMachine.from("idle" /* Idle */).to(
    // Play is triggered, and the player needs to load, maybe seek, buffer,
    // before the media actually starts playing.
    "starting" /* Starting */,
    // The media is found broken or unavailable when preloading, before play is
    // triggered.
    "broken" /* Broken */
  );
  stateMachine.from("starting" /* Starting */).to(
    // The happy path. Playback starts normally after being loaded/buffered.
    "playing" /* Playing */,
    // Pause is triggered before playback has a chance to start. If play has
    // not actually been called on the media element yet, it must not be
    // called.
    "paused" /* Paused */,
    // Playback couldn't start because of autoplay policy.
    "autoplayblocked" /* AutoplayBlocked */,
    // The media is found broken or unavailable after play was triggered,
    // before playback has started.
    "broken" /* Broken */
  );
  stateMachine.from("playing" /* Playing */).to(
    // The user seeks to another position while the media is playing.
    "seeking" /* Seeking */,
    // The network is slow, causing rebuffering.
    "rebuffering" /* Rebuffering */,
    // Pause is triggered.
    "paused" /* Paused */,
    // Playback was paused bacause an autoplayed session was unmuted without
    // user interaction.
    "autoplayblocked" /* AutoplayBlocked */,
    // The end of on-demand media is reached.
    "ended" /* Ended */,
    // Playback stops unexpectedly and can't be resumed.
    "broken" /* Broken */
  );
  stateMachine.from("seeking" /* Seeking */).to(
    // The player resumes playing after a seek.
    "playing" /* Playing */,
    // Pause is triggered while the player is seeking. The player could
    // continue to rebuffer in the background, but it must not start playing
    // afterwards.
    "paused" /* Paused */,
    // The seek was to the end of an on-demand stream.
    "ended" /* Ended */,
    // Something broke and playback can't be resumed.
    "broken" /* Broken */
  );
  stateMachine.from("rebuffering" /* Rebuffering */).to(
    // The player resumes playing after rebuffering.
    "playing" /* Playing */,
    // The user seeks to another position during rebuffering.
    "seeking" /* Seeking */,
    // Pause is triggered while rebuffering. The player could continue to
    // rebuffer in the background, but it must not start playing afterwards.
    "paused" /* Paused */,
    // The rebuffering happened just at the end of an on-demand stream.
    //
    // Unclear: Will this actually happen? Does it matter?
    "ended" /* Ended */,
    // Something broke and playback can't be resumed.
    "broken" /* Broken */
  );
  stateMachine.from("paused" /* Paused */).to(
    // Play is triggered while paused and playback starts immediately.
    "playing" /* Playing */,
    // Play is triggered while paused but playback doesn't start immediately.
    "rebuffering" /* Rebuffering */,
    // Seeking is triggered while paused.
    "paused-seeking" /* PausedSeeking */,
    // A pause event was initially not correctly detected as ended, but being
    // stuck in paused is no good. So allow going from paused to paused with
    // ended.
    "ended" /* Ended */,
    // Something broke and playback can't be resumed.
    "broken" /* Broken */
  );
  stateMachine.from("autoplayblocked" /* AutoplayBlocked */).to(
    // Play is triggered and playback starts immediately.
    "playing" /* Playing */,
    // Play is triggered but playback doesn't start immediately.
    "rebuffering" /* Rebuffering */,
    // Seeking is triggered while blocked by autoplay policy (this ok?).
    "paused-seeking" /* PausedSeeking */,
    // Something broke and playback can't be resumed.
    "broken" /* Broken */
  );
  stateMachine.from("paused-seeking" /* PausedSeeking */).to(
    // Play is triggered while seeking.
    "seeking" /* Seeking */,
    // Seeking ended while still paused. Only allowed if the previous state was
    // AdapterState.Paused.
    "paused" /* Paused */,
    // Seeking ended while still blocked by autoplay policy. Only allowed if
    // the previous state was AdapterState.AutoplayBlocked.
    "autoplayblocked" /* AutoplayBlocked */,
    // The seek was to the end of an on-demand stream.
    "ended" /* Ended */,
    // Something broke and playback can't be resumed.
    "broken" /* Broken */
  );
  stateMachine.from("ended" /* Ended */).to(
    // Play is triggered (replay) and playback starts immediately.
    "playing" /* Playing */,
    // Play is triggered (replay) but playback doesn't start immediately.
    "rebuffering" /* Rebuffering */,
    // Seeking is triggered while paused.
    "paused-seeking" /* PausedSeeking */
  );
}

// src/adapter/machine/state-change-events.ts
function addStateChangeEvents(stateMachine, emitter, dataProvider) {
  function onTransition(from, to, listener) {
    stateMachine.on(to, (actualFrom) => {
      if (from === actualFrom) {
        listener();
      }
    });
  }
  let playIsReplay = false;
  const emitPlaying = () => {
    if (playIsReplay) {
      playIsReplay = false;
      emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: true, type: "replay" /* Replay */ });
    } else {
      emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: true });
    }
  };
  onTransition("idle" /* Idle */, "starting" /* Starting */, () => {
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: true, type: "starting" /* Starting */ });
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "starting" /* Starting */ });
  });
  onTransition("idle" /* Idle */, "broken" /* Broken */, () => {
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("starting" /* Starting */, "playing" /* Playing */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "starting" /* Starting */ });
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: true, time: dataProvider.getStartTime() });
  });
  onTransition("starting" /* Starting */, "paused" /* Paused */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "starting" /* Starting */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
  });
  onTransition("starting" /* Starting */, "autoplayblocked" /* AutoplayBlocked */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "starting" /* Starting */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "autoplayblocked" /* AutoplayBlocked */ });
  });
  onTransition("starting" /* Starting */, "broken" /* Broken */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "starting" /* Starting */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("playing" /* Playing */, "seeking" /* Seeking */, () => {
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: false, time: dataProvider.getStopTime() });
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "seeking" /* Seeking */ });
  });
  onTransition("playing" /* Playing */, "rebuffering" /* Rebuffering */, () => {
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: false, time: dataProvider.getStopTime() });
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "rebuffering" /* Rebuffering */ });
  });
  onTransition("playing" /* Playing */, "paused" /* Paused */, () => {
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: false, time: dataProvider.getStopTime() });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
  });
  onTransition("playing" /* Playing */, "autoplayblocked" /* AutoplayBlocked */, () => {
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: false, time: dataProvider.getStopTime() });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "autoplayblocked" /* AutoplayBlocked */ });
  });
  onTransition("playing" /* Playing */, "ended" /* Ended */, () => {
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: false, time: dataProvider.getStopTime() });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "ended" /* Ended */ });
  });
  onTransition("playing" /* Playing */, "broken" /* Broken */, () => {
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: false, time: dataProvider.getStopTime() });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("seeking" /* Seeking */, "playing" /* Playing */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: true, time: dataProvider.getStartTime() });
  });
  onTransition("seeking" /* Seeking */, "paused" /* Paused */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
  });
  onTransition("seeking" /* Seeking */, "ended" /* Ended */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "ended" /* Ended */ });
  });
  onTransition("seeking" /* Seeking */, "broken" /* Broken */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("rebuffering" /* Rebuffering */, "playing" /* Playing */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "rebuffering" /* Rebuffering */ });
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: true, time: dataProvider.getStartTime() });
  });
  onTransition("rebuffering" /* Rebuffering */, "seeking" /* Seeking */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "seeking" /* Seeking */ });
  });
  onTransition("rebuffering" /* Rebuffering */, "paused" /* Paused */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "rebuffering" /* Rebuffering */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
  });
  onTransition("rebuffering" /* Rebuffering */, "ended" /* Ended */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "rebuffering" /* Rebuffering */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "ended" /* Ended */ });
  });
  onTransition("rebuffering" /* Rebuffering */, "broken" /* Broken */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "rebuffering" /* Rebuffering */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false });
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("paused" /* Paused */, "playing" /* Playing */, () => {
    emitPlaying();
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: true, time: dataProvider.getStartTime() });
  });
  onTransition("paused" /* Paused */, "rebuffering" /* Rebuffering */, () => {
    emitPlaying();
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "rebuffering" /* Rebuffering */ });
  });
  onTransition("paused" /* Paused */, "paused-seeking" /* PausedSeeking */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "seeking" /* Seeking */ });
  });
  onTransition("paused" /* Paused */, "ended" /* Ended */, () => {
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "ended" /* Ended */ });
  });
  onTransition("paused" /* Paused */, "broken" /* Broken */, () => {
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("autoplayblocked" /* AutoplayBlocked */, "playing" /* Playing */, () => {
    emitPlaying();
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: true, time: dataProvider.getStartTime() });
  });
  onTransition("autoplayblocked" /* AutoplayBlocked */, "rebuffering" /* Rebuffering */, () => {
    emitPlaying();
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "rebuffering" /* Rebuffering */ });
  });
  onTransition("autoplayblocked" /* AutoplayBlocked */, "paused-seeking" /* PausedSeeking */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "seeking" /* Seeking */ });
  });
  onTransition("autoplayblocked" /* AutoplayBlocked */, "broken" /* Broken */, () => {
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("paused-seeking" /* PausedSeeking */, "seeking" /* Seeking */, () => {
    emitPlaying();
  });
  onTransition("paused-seeking" /* PausedSeeking */, "paused" /* Paused */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
  });
  onTransition("paused-seeking" /* PausedSeeking */, "autoplayblocked" /* AutoplayBlocked */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
  });
  onTransition("paused-seeking" /* PausedSeeking */, "ended" /* Ended */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
    emitter.emit("playercore.playingchange" /* PlayingChange */, { isPlaying: false, pausedReason: "ended" /* Ended */ });
  });
  onTransition("paused-seeking" /* PausedSeeking */, "broken" /* Broken */, () => {
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: false, reason: "seeking" /* Seeking */ });
    emitter.emit("playercore.broken" /* Broken */, { error: dataProvider.getError() });
  });
  onTransition("ended" /* Ended */, "playing" /* Playing */, () => {
    playIsReplay = true;
    emitPlaying();
    emitter.emit("playercore.timerunningchange" /* TimeRunningChange */, { isTimeRunning: true, time: dataProvider.getStartTime() });
  });
  onTransition("ended" /* Ended */, "rebuffering" /* Rebuffering */, () => {
    playIsReplay = true;
    emitPlaying();
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "rebuffering" /* Rebuffering */ });
  });
  onTransition("ended" /* Ended */, "paused-seeking" /* PausedSeeking */, () => {
    playIsReplay = true;
    emitter.emit("playercore.waitingchange" /* WaitingChange */, { isWaiting: true, reason: "seeking" /* Seeking */ });
  });
}

// src/adapter/machine/index.ts
var logger = getLogger("AdapterFSM");
function createAdapterStateMachine(emitter, dataProvider) {
  const stateMachine = new TypeState.FiniteStateMachine("idle" /* Idle */);
  addStateTransitions(stateMachine);
  addStateChangeEvents(stateMachine, emitter, dataProvider);
  stateMachine.onTransition = (from, to) => {
    logger.log(`Transitioned from ${from} to ${to}.`);
  };
  stateMachine.onInvalidTransition((from, to) => {
    logger.log(`Blocked adapter state transition from ${from} to ${to}.`);
    return true;
  });
  return stateMachine;
}

// src/adapter/adapter-base-abstract.ts
import { DestroyableSet } from "@nrk/destroyable";
var logger2 = getLogger("AdapterBaseAbstract");
var AdapterBaseAbstract = class extends DestroyableSet {
  /**
   * Must be set before destroying the adapter from within, and only when
   * destroying the adapter from within. PlaybackSession listens for the
   * adapter 'destroy' event and adds the reason to the SessionClosed event.
   */
  destroyedReason;
  eventEmitter;
  fatalError;
  stateMachine;
  subtitleControls;
  /**
   * True if playback ever succeeded.
   */
  playbackStarted = false;
  constructor(eventEmitter) {
    super();
    this.eventEmitter = eventEmitter;
    this.stateMachine = createAdapterStateMachine(eventEmitter, {
      getError: this.getError.bind(this),
      getStartTime: this.getTimeStartedPosition.bind(this),
      getStopTime: this.getTimeStoppedPosition.bind(this)
    });
    const previousTransitionHandler = this.stateMachine.onTransition;
    this.stateMachine.onTransition = (from, to) => {
      if (from === "playing" /* Playing */ && this.fatalError !== void 0) {
        if (to !== "broken" /* Broken */ && to !== "ended" /* Ended */) {
          logger2.log("Going to broken as playback halted after a fatal error.");
          this.stateMachine.go("broken" /* Broken */);
        }
      } else if (to === "playing" /* Playing */) {
        this.playbackStarted = true;
      }
      previousTransitionHandler(from, to);
    };
  }
  // Translate adapter states to session states.
  getState() {
    return adapterToPlaybackState(this.stateMachine.currentState).playbackState;
  }
  getWaitingReason() {
    return adapterToPlaybackState(this.stateMachine.currentState).waitingReason;
  }
  getPausedReason() {
    return adapterToPlaybackState(this.stateMachine.currentState).pausedReason;
  }
  getError() {
    return this.fatalError;
  }
  setNoSubtitles(options) {
    this.subtitleControls?.setNoSubtitles(options);
  }
  setDefaultSubtitles() {
    this.subtitleControls?.setDefaultSubtitles();
  }
  setAutoSubtitles() {
    this.subtitleControls?.setAutoSubtitles();
  }
  setActiveSubtitles(id, options) {
    this.subtitleControls?.setActiveSubtitles(id, options);
  }
  getActiveSubtitles() {
    return this.subtitleControls?.getActiveSubtitles();
  }
  getAvailableSubtitles() {
    return this.subtitleControls?.getAvailableSubtitles() ?? [];
  }
  enableNativeSubtitlesDisplay() {
    this.subtitleControls?.enableNativeSubtitlesDisplay();
  }
  disableNativeSubtitlesDisplay() {
    this.subtitleControls?.disableNativeSubtitlesDisplay();
  }
  isNativeSubtitlesDisplayEnabled() {
    return this.subtitleControls?.isNativeSubtitlesDisplayEnabled() ?? false;
  }
  // Error handling.
  handleError(error) {
    if (!error.fatal) {
      logger2.log("Got non-fatal error:", error, error.cause);
      return;
    }
    if (this.fatalError !== void 0) {
      logger2.log("Got follow-up fatal error:", error, error.cause);
      return;
    }
    logger2.log("Got fatal error:", error, error.cause);
    this.fatalError = error;
    if (this.stateMachine.is("playing" /* Playing */)) {
      logger2.log("Saving fatal error for later when time stops.");
    } else {
      logger2.log("Time is not running. Changing to broken.");
      this.stateMachine.go("broken" /* Broken */);
    }
  }
  // Destroy.
  destroy() {
    this.subtitleControls?.destroy?.();
    super.destroy();
  }
};

// src/adapter/adapter-base-time.ts
var logger3 = getLogger("AdapterTimeBase");
var AdapterBaseTime = class extends AdapterBaseAbstract {
  liveOptions;
  /**
   * The current range of the media, as reported via
   * `this.handleMediaRangeUpdate()`, not directly.
   */
  mediaRange;
  /**
   * The UTC date/time of the stream content at position 0, in seconds since
   * epoch. Used for calculating the date/time of any position in the stream.
   *
   * It should be set via `this.handleMediaRangeUpdate()`, not directly.
   */
  programDateTimeAtPosZero;
  /**
   * True if the media was live when initially loaded.
   */
  isLive;
  /**
   * True if the media range is no longer expected to be updated, if `isLive`
   * is true.
   */
  isLiveEnded;
  /**
   * The UTC date/time (wall clock) of the client, and the position into the
   * media range that playback should not pass in order to avoid rebuffering,
   * at the time live sliding started.
   *
   * `clientDateTime` is the number of seconds since epoch.
   *
   * `livePosition` might be negative if there are fewer than the ideal buffer
   * size segments at start. It might be `undefined` if the browser doesn't
   * provide a usable number (`Infinity`).
   */
  slidingStart;
  /**
   * If the media range moves at both ends (assumed to move synchronously),
   * then the sliding window duration decides the duration of the scrubber
   * range over the media range, normally ending at the live position. It might
   * be larger than the media range, creating a non-seekable hole at the
   * beginning (left end) of the scrubber.
   *
   * If it's undefined, then the media range is growing. If it's 0, then
   * seeking is not supported, regardless of stream type.
   */
  slidingWindowDuration;
  constructor(eventEmitter, liveOptions) {
    super(eventEmitter);
    this.liveOptions = liveOptions;
    this.slidingWindowDuration = liveOptions?.slidingWindowDuration;
  }
  isLiveStream() {
    return this.isLive ?? false;
  }
  isLiveStreamEnded() {
    return this.isLiveEnded ?? false;
  }
  isSeekable() {
    return this.slidingWindowDuration !== 0;
  }
  getCurrentTimeLive() {
    return this.positionToLiveTime(this.getCurrentTime());
  }
  seekToRelativeTime(relativeTime) {
    this.seekTo(this.getCurrentTime() + relativeTime);
  }
  seekToLive() {
    if (this.isLiveStream() && !this.isLiveStreamEnded()) {
      const seekableRange = this.getSeekableRange();
      if (this.getCurrentTime() < seekableRange.end) {
        this.seekTo(seekableRange.end);
      }
    }
  }
  seekToLiveTime(liveTime) {
    if (this.isLiveStream()) {
      this.seekTo(this.liveTimeToPosition(liveTime));
    }
  }
  /**
   * If `programDateTimeAtPosZero` isn't set, then the output will be
   * completely wrong.
   */
  positionToLiveTime(position) {
    return new Date(((this.programDateTimeAtPosZero ?? 0) + position) * 1e3);
  }
  /**
   * If `programDateTimeAtPosZero` isn't set, then the output will be
   * completely wrong.
   */
  liveTimeToPosition(liveTime) {
    return liveTime.getTime() / 1e3 - (this.programDateTimeAtPosZero ?? 0);
  }
  /**
   * Returns the reported or the de facto available media range.
   */
  getMediaRange() {
    return this.mediaRange ?? { start: 0, end: this.getCurrentTime() };
  }
  /**
   * Returns the range of the scrubber. For on-demand it's equal to the media
   * range. For live it's sliding or gradually expanding with time, depending
   * on the type of stream.
   */
  getScrubberRange() {
    const mediaRange = this.getMediaRange();
    if (!this.isLiveStream() || this.isLiveStreamEnded() || this.slidingStart?.livePosition === void 0) {
      return mediaRange;
    }
    const end = Math.max(this.getCurrentTime(), this.getLivePosition());
    return { start: this.slidingWindowDuration === void 0 ? 0 : end - this.slidingWindowDuration, end };
  }
  getLivePosition() {
    if (this.slidingStart?.livePosition === void 0) {
      return 0;
    }
    const sliding = Date.now() / 1e3 - this.slidingStart.clientDateTime;
    return this.slidingStart.livePosition + sliding;
  }
  /**
   * The seekable range is the intersection of the media and scrubber ranges.
   */
  getSeekableRange() {
    const mediaRange = this.getMediaRange();
    if (!this.isLiveStream() || this.isLiveStreamEnded()) {
      return mediaRange;
    }
    const scrubberRange = this.getScrubberRange();
    const start = Math.max(mediaRange.start, scrubberRange.start);
    const end = Math.min(mediaRange.end, scrubberRange.end);
    if (start > end) {
      return scrubberRange;
    }
    return { start, end };
  }
  /**
   * Returns a set of values for convenient rendering of a scrubber. Can be
   * polled to produce sliding effects for live stream scrubbers and the
   * playhead (also on-demand), although it's probably better to listen to the
   * `ScrubberUpdate` events.
   */
  getScrubberValues() {
    const scrubberRange = this.getScrubberRange();
    const currentTime = this.getCurrentTime();
    const playheadPosition = Math.max(currentTime, scrubberRange.start);
    if (!this.isLiveStream() || this.programDateTimeAtPosZero === void 0) {
      return {
        startPosition: scrubberRange.start,
        startDisplayTime: scrubberRange.start,
        endPosition: scrubberRange.end,
        endDisplayTime: scrubberRange.end,
        playheadPosition,
        playheadDisplayTime: playheadPosition
      };
    }
    const startLiveTime = this.positionToLiveTime(scrubberRange.start);
    const endLiveTime = this.positionToLiveTime(scrubberRange.end);
    const playheadLiveTime = this.positionToLiveTime(playheadPosition);
    const startDisplayTime = (startLiveTime.getTime() / 1e3 - startLiveTime.getTimezoneOffset() * 60) % 86400;
    const endDisplayTime = (endLiveTime.getTime() / 1e3 - endLiveTime.getTimezoneOffset() * 60) % 86400;
    const playheadDisplayTime = (playheadLiveTime.getTime() / 1e3 - playheadLiveTime.getTimezoneOffset() * 60) % 86400;
    return {
      startPosition: scrubberRange.start,
      startLiveTime,
      startDisplayTime,
      endPosition: scrubberRange.end,
      endLiveTime,
      endDisplayTime,
      playheadPosition,
      playheadLiveTime,
      playheadDisplayTime
    };
  }
  /**
   * Subclass must call this when the media range is known and updated, and
   * when the program date/time is known (or updated).
   */
  handleMediaRangeUpdate(mediaRange, livePosition, programDateTime) {
    this.mediaRange = mediaRange;
    if (this.isLive === void 0) {
      this.isLive = !mediaRange.complete;
    }
    if (!this.isLive) {
      return;
    }
    let emitLiveStreamEndedEvent = false;
    if (mediaRange.complete && !this.isLiveEnded) {
      this.isLiveEnded = true;
      emitLiveStreamEndedEvent = true;
    }
    if (programDateTime !== void 0) {
      this.programDateTimeAtPosZero = programDateTime - mediaRange.start + (this.liveOptions?.programDateTimeCorrection ?? 0);
    }
    if (this.slidingStart?.livePosition === void 0) {
      this.slidingStart = {
        livePosition,
        clientDateTime: mediaRange.timestamp
      };
    } else if (livePosition !== void 0) {
      const expectedLivePosition = this.slidingStart.livePosition + mediaRange.timestamp - this.slidingStart.clientDateTime;
      if (livePosition > expectedLivePosition || livePosition < expectedLivePosition - 60) {
        const improvement = livePosition - expectedLivePosition;
        logger3.log(`Adjusting live position to be ${improvement} seconds closer to real-time.`);
        this.slidingStart = {
          livePosition,
          clientDateTime: mediaRange.timestamp
        };
      }
    }
    if (this.slidingWindowDuration === void 0 && mediaRange.start > 1) {
      this.slidingWindowDuration = niceSlidingWindowDuration(mediaRange.end - mediaRange.start);
      logger3.log(`Detected sliding window. Scrubber duration set to: ${this.slidingWindowDuration}`);
    }
    if (emitLiveStreamEndedEvent) {
      this.eventEmitter.emit("playercore.livestreamended" /* LiveStreamEnded */, this.getMediaRange());
    }
  }
  /**
   * Program date/time for live/event HLS streams which don't provide one. The
   * returned value represents the date/time at media range start.
   *
   * It takes n seconds to produce an n seconds long segment. Then the segment
   * needs to be made available in the media playlist and the playlist loaded by
   * the client. The delay is a very rough estimate. In general, it's best if the
   * player clock is slightly behind the stream time, so that navigation to a
   * date/time seeks to a position just be fore actual content.
   */
  guessHlsProgramDateTime(playlistDuration, segmentDuration) {
    const delay = this.liveOptions?.liveDelay ?? segmentDuration + 9;
    return Date.now() / 1e3 - delay - playlistDuration;
  }
};
function niceSlidingWindowDuration(mediaRangeDuration) {
  const r = 60;
  const minuteBin = 5;
  const minutes = Math.floor(mediaRangeDuration / r);
  const seconds = mediaRangeDuration % r;
  const binnedMinutes = Math.floor(minutes / minuteBin) * minuteBin;
  if (minutes - binnedMinutes === minuteBin - 1 && seconds >= r / 2) {
    return (binnedMinutes + minuteBin) * r;
  }
  return minutes * r;
}

// src/utils/implements-static.ts
function implementsStatic() {
  return (constructor) => {
    constructor;
  };
}

// src/utils/time-range.ts
function convertTimeRanges(ranges) {
  const list = [];
  for (let i = 0; i < ranges.length; ++i) {
    list.push({ start: ranges.start(i), end: ranges.end(i) });
  }
  return list;
}
function toWithinRange(range, value) {
  if (value < range.start) {
    return range.start;
  }
  if (value > range.end) {
    return range.end;
  }
  return value;
}

// src/utils/client-language.ts
function getClientLanguages() {
  return navigator.languages ? navigator.languages : navigator.language ? [navigator.language] : [];
}
function getMatchLanguages(tags) {
  const uniqueTags = new Set(tags);
  tags.forEach((tag) => uniqueTags.add(getPrimarySubtag(tag)));
  const resultTags = Array.from(uniqueTags);
  if (!uniqueTags.has("no")) {
    const firstMatch = resultTags.findIndex((tag) => /^(nb|nn|se)$/.exec(tag));
    if (firstMatch >= 0) {
      resultTags.splice(firstMatch + 1, 0, "no");
    }
  }
  return resultTags;
}
function orderByLanguage(items, getItemLanguage, preferredLanguages) {
  const afterPreferredIndex = preferredLanguages.length;
  const preferredIndexMap = items.map((item, itemIndex) => {
    const language = getItemLanguage(item);
    const fullMatchIndex = preferredLanguages.indexOf(language);
    if (fullMatchIndex !== -1) {
      return [fullMatchIndex, itemIndex];
    }
    const primarySubtagIndex = preferredLanguages.indexOf(getPrimarySubtag(language));
    if (primarySubtagIndex !== -1) {
      return [primarySubtagIndex, itemIndex];
    }
    return [afterPreferredIndex, itemIndex];
  });
  preferredIndexMap.sort((a, b) => a[0] - b[0]);
  return preferredIndexMap.map((indexes) => items[indexes[1]]);
}
function getPrimarySubtag(tag) {
  return tag.replace(/^((?:\w-)?\w+).*$/, "$1");
}

export {
  AdapterState,
  adapterToPlaybackState,
  AdapterBaseAbstract,
  AdapterBaseTime,
  getClientLanguages,
  getMatchLanguages,
  orderByLanguage,
  implementsStatic,
  convertTimeRanges,
  toWithinRange
};
